import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Users, MapPin, Clock, CheckCircle2, Star, ChevronDown, ChevronUp, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import PSPlastyCatering from '../components/PSPlastyCatering';

// PS Plasty 20th Anniversary - Interactive B2B Offer
// Agency: VŠE PRO AKCI s.r.o. | Client: PS Plasty
// Key Feature: Interactive budget with checkboxes - client can deselect items to save money

const PSPlastyPage = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Guest count state for catering calculation
  const [guestCount, setGuestCount] = useState(80);
  const [cateringTotal, setCateringTotal] = useState(0); // Celková cena z PSPlastyCatering komponenty
  // Program State (Interactive Checkboxes)
  const [programItems, setProgramItems] = useState({
    // FIXED - Cannot be removed
    dj_moderator: { selected: true, fixed: true, price: 25000, name: 'Moderátor a DJ (15:00-2:00)' },
    
    // Artists - Optional
    party_leaders: { selected: true, price: 39500, name: 'Kapela Párty Leaders 180 min. + doprava (Brno, Ostrava)', recommended: true },
    abba_rock: { selected: false, price: 38000, name: 'Kapela Abba Rock show Zlín (4 muzikanti, 60 min.)', recommended: true },
    acdc_revival: { selected: false, price: 100000, name: 'AC/DC revival - nejlepší v ČR', recommended: true },
    kabat_revival: { selected: false, price: 23000, name: 'Kapela Tabák (Kabát revival) - 4 muzikanti, 60 min.' },
    brazilian_dancers: { selected: true, price: 35500, name: 'Brazilské tanečnice Tradicion + doprava (Praha) - 3 tanečnice, 2 vstupy', recommended: true },
    anta_agni: { selected: false, price: 32000, name: 'Světelná show Anta Agni (Bratislava) - LED Dream + UV Light, 2 vstupy', recommended: true },
    artem_dance: { selected: false, price: 12000, name: 'Taneční vstup Artem Dance Company (10 tanečnic, 2 vstupy)' },
    slackline: { selected: false, price: 37000, name: 'Slack Line show + doprava (Praha) - 2 akrobaté, 2 vstupy', recommended: true },
    obinn_stage: { selected: true, price: 7000, name: 'Obinn Magic - Stage show' },
    obinn_closeup: { selected: true, price: 22000, name: 'Obinn Magic - iluze mezi hosty (spojení s produkty PS-PLASTY)' },
    ritmo_factory: { selected: false, price: 37000, name: 'Ritmo Factory - bubenická show', recommended: true },
    
    // Interactive Zone (moved from Silent Power Arena)
    rally: { selected: false, price: 28500, name: 'Profi simulátor Rally (1 auto, 4 hod, instruktor)', recommended: true },
    f1: { selected: false, price: 14000, name: 'Simulátor Formule 1 (1 zařízení, 4 hod, instruktor)', recommended: true },
    vr_1: { selected: false, price: 11000, name: 'Virtuální realita - 1 zařízení (4 hod, TV, instruktor)' },
    vr_2: { selected: false, price: 20000, name: 'Virtuální realita - 2 zařízení (4 hod, TV, instruktor)' },
    hostess: { selected: false, price: 1200, name: 'Hosteska v závodním dresu (4 hod)' },
    winners_podium: { selected: false, price: 2500, name: 'Stupně vítězů a poháry pro vítěze' },
    foosball: { selected: false, price: 2500, name: 'Stolní fotbálek' }
  });

  // Catering State - removed (now using static table data)

  // Technical State
  const [technicalItems, setTechnicalItems] = useState({
    // VIP Stan - všechny položky jsou nyní volitelné
    tent: { selected: true, price: 20000, name: 'Modulární párty stan 10×15m' },
    floor: { selected: true, price: 20000, name: 'Dřevěná podlaha 10×15m' },
    tables: { selected: true, price: 5000, name: '10 kulatých stolů vč. potahu' },
    chairs: { selected: true, price: 8000, name: '80 židlí vč. potahu' },
    podium: { selected: true, price: 10800, name: 'Pódium 6×4m' },
    led_panels: { selected: false, price: 20000, name: 'LED panely ve stanu' },
    sound: { selected: true, price: 12000, name: 'Ozvučení stanu (může se změnit dle výběru umělců)' },
    lighting: { selected: true, price: 6000, name: 'Osvětlení stanu (může se změnit dle výběru umělců)' },
    
    // Venkovní prostory
    bistro_tables: { selected: true, price: 3500, name: '10 bistro stolků vč. potahu' },
    activity_tents: { selected: true, price: 10000, name: '2× stan 8×4m (zastřešení aktivit)' },
    outdoor_led: { selected: false, price: 35000, name: 'LED obrazovka - outdoor' },
    
    // Personál
    assembly: { selected: true, price: 12000, name: 'Montáž a demontáž (4 osoby)' },
    transport: { selected: true, price: 10000, name: 'Doprava (cena odhad) - bude vyčíslena na základě dodané techniky' },
    sound_tech: { selected: true, price: 6000, name: 'Zvukař (15:00-2:00)' },
    light_tech: { selected: true, price: 6000, name: 'Osvětlovač (15:00-2:00)' },
    // LED screen technik - automaticky přidán pokud je vybrána LED obrazovka
    led_tech: { selected: false, price: 6000, name: 'LED screen technik (15:00-2:00)', isAutomatic: true, dependsOn: ['led_panels', 'outdoor_led'] }
  });

  // Decoration State
  const [decorationItems, setDecorationItems] = useState({
    balloon_arch: { selected: true, price: 3500, name: 'Balonková výzdoba - oblouk - vstup do párty stanu' },
    balloon_columns: { selected: true, price: 3000, name: 'Balonkové sloupy na podiu 2ks x 1500,-' },
    orange_runners: { selected: true, price: 500, name: 'Oranžový běhoun 10x 50,-' },
    vases_flowers: { selected: true, price: 2000, name: 'Vázička a decentní kytka na stole 10x 200,-' },
    chair_bows: { selected: true, price: 2800, name: 'Mašle na židle 80x 35,-' },
    bistro_bows: { selected: true, price: 350, name: 'Mašle na bistrostolek 10x 35,-' },
    black_buffet_skirts: { selected: true, price: 1200, name: 'Černé rautové sukně 3x 400,- včetně praní a žehlení' },
    helium_balloons: { selected: true, price: 1000, name: 'Heliové balonky na závaží - trsy - v prostoru párty stanu' },
    cake_model: { selected: true, price: 7000, name: 'Maketa dortu 2,5m - pronájem' },
    cupcakes: { selected: true, price: 2800, name: 'Cup cake 80x 35,- s logem firmy /na maketě dortu naaranžované/' },
    light_number_20: { selected: true, price: 2500, name: 'Svítící číslo 20' },
    decoration_personnel: { selected: true, price: 10000, name: 'Personál na dekoraci prostor 4 osoby × 250 Kč/hod × 10 hod (stoly, židle apod)' }
  });

  // Graphics State
  const [graphicsItems, setGraphicsItems] = useState({
    graphic_design: { selected: true, price: 3500, name: 'Grafické zpracování pozvánky, apod. dle zadání' },
    printing: { selected: true, price: 2500, name: 'Tisky, výroba tiskovin a pvc banerů - označení' }
  });

  // Production - Fixed coordination fee only
  const production = useMemo(() => {
    return {
      coordination: 25000
    };
  }, []);

  // Toggle Functions
  const toggleProgramItem = (key) => {
    if (programItems[key].fixed) return;
    setProgramItems(prev => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key].selected }
    }));
  };

  const toggleTechnicalItem = (key) => {
    setTechnicalItems(prev => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key].selected }
    }));
  };

  // Automaticky přidat/odebrat LED screen technika podle LED obrazovek
  useEffect(() => {
    const hasLedScreen = technicalItems.led_panels?.selected || technicalItems.outdoor_led?.selected;
    
    if (technicalItems.led_tech?.isAutomatic) {
      setTechnicalItems(prev => ({
        ...prev,
        led_tech: { ...prev.led_tech, selected: hasLedScreen }
      }));
    }
  }, [technicalItems.led_panels?.selected, technicalItems.outdoor_led?.selected]);

  const toggleDecorationItem = (key) => {
    setDecorationItems(prev => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key].selected }
    }));
  };

  const toggleGraphicsItem = (key) => {
    setGraphicsItems(prev => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key].selected }
    }));
  };

  // Calculate Totals
  const calculateTotals = () => {
    const programTotal = Object.values(programItems)
      .filter(item => item.selected)
      .reduce((sum, item) => sum + item.price, 0);
    
    // Catering - použít hodnotu z PSPlastyCatering komponenty (přes setCateringTotal)
    // Není potřeba počítat zde, hodnota je nastavena z komponenty
    
    const technicalTotal = Object.values(technicalItems)
      .filter(item => item.selected)
      .reduce((sum, item) => sum + item.price, 0);
    
    const decorationTotal = Object.values(decorationItems)
      .filter(item => item.selected)
      .reduce((sum, item) => sum + item.price, 0);
    
    const graphicsTotal = Object.values(graphicsItems)
      .filter(item => item.selected)
      .reduce((sum, item) => sum + item.price, 0);
    
    const productionTotal = production.coordination;
    
    return {
      program: programTotal,
      catering: cateringTotal,
      technical: technicalTotal,
      decoration: decorationTotal,
      graphics: graphicsTotal,
      production: productionTotal,
      total: programTotal + cateringTotal + technicalTotal + decorationTotal + graphicsTotal + productionTotal
    };
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-[#020617]">
      <HeroSection />
      <IntroSection />
      <DramaturgieSection 
        programItems={programItems}
        toggleProgramItem={toggleProgramItem}
        production={production}
      />
      <VideoGallerySection />
      <PSPlastyCatering 
        guestCount={guestCount} 
        setGuestCount={setGuestCount}
        onTotalChange={setCateringTotal}
      />
      <TechnicalSection 
        technicalItems={technicalItems}
        toggleTechnicalItem={toggleTechnicalItem}
      />
      <DecorationSection 
        decorationItems={decorationItems}
        toggleDecorationItem={toggleDecorationItem}
      />
      <GraphicsSection 
        graphicsItems={graphicsItems}
        toggleGraphicsItem={toggleGraphicsItem}
      />
      <GrandTotalSection 
        totals={totals}
        programItems={programItems}
        technicalItems={technicalItems}
        decorationItems={decorationItems}
        graphicsItems={graphicsItems}
        production={production}
      />
      <PromoVideoSection />
      <WhyUsSection />
      <ContactSection />
    </div>
  );
};

// HERO
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 z-0">
      <img 
        src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/u04l3j1y_dekorace.jpg"
        alt="Silent Czech Event - Firemní akce s černo-oranžovou výzdobou"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#020617]" />
    </div>

    <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-24 text-center">
      <div className="mb-12 flex justify-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-[#FF6B00]/30">
          <img 
            src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/qp0a593q_PS%20PLASTY.png"
            alt="PS Plasty"
            className="h-16 w-auto"
          />
        </div>
      </div>

      <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 drop-shadow-2xl" style={{
        textShadow: '0 0 40px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.8)'
      }}>
        20 let PS Plasty
      </h1>

      <div className="text-2xl md:text-3xl font-semibold mb-12 drop-shadow-xl" style={{
        background: 'linear-gradient(135deg, #FF6B00 0%, #FFB800 50%, #FF6B00 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: '0 2px 20px rgba(0,0,0,0.8)'
      }}>
        Výročí úspěchu a růstu
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
        <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          <Calendar className="w-8 h-8 text-[#FF6B00] mb-3 mx-auto" />
          <div className="text-3xl font-bold text-white mb-2">18.9.2026</div>
          <div className="text-slate-400 text-sm">Čtvrtek</div>
        </div>
        <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          <Clock className="w-8 h-8 text-[#FF6B00] mb-3 mx-auto" />
          <div className="text-3xl font-bold text-white mb-2">15:00–2:00</div>
          <div className="text-slate-400 text-sm">Délka akce</div>
        </div>
        <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          <Users className="w-8 h-8 text-[#FF6B00] mb-3 mx-auto" />
          <div className="text-3xl font-bold text-white mb-2">~80</div>
          <div className="text-slate-400 text-sm">Hostů</div>
        </div>
        <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          <MapPin className="w-8 h-8 text-[#FF6B00] mb-3 mx-auto" />
          <div className="text-xl font-bold text-white mb-2">Areál firmy</div>
          <div className="text-slate-400 text-sm">PS Plasty</div>
        </div>
      </div>
    </div>
  </section>
);

// INTRO
const IntroSection = () => (
  <section className="py-24 px-6 bg-gradient-to-b from-[#020617] to-slate-950">
    <div className="max-w-6xl mx-auto">
      <div className="bg-slate-900/40 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Koncept akce
        </h2>
        <p className="text-xl text-slate-300 leading-relaxed mb-6">
          Výjimečná firemní oslava <strong className="text-[#FF6B00]">20. výročí společnosti PS Plasty</strong> - prémiový event 
          pro cca <strong>80 hostů</strong> (zaměstnanci a zákazníci).
        </p>
        <p className="text-lg text-slate-400 leading-relaxed mb-8">
          <strong className="text-white">Místo:</strong> Areál firmy PS Plasty<br/>
          <strong className="text-white">Dominanta:</strong> VIP party stan (indoor + outdoor prostory)<br/>
          <strong className="text-white">Charakter:</strong> Prémiový, slavnostní firemní event<br/>
          <strong className="text-white">Tématika:</strong> 20 let úspěchu a růstu
        </p>

        <div className="bg-[#FF6B00]/10 border-l-4 border-[#FF6B00] p-6 rounded-r-xl">
          <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <Star className="w-6 h-6 text-[#FF6B00]" />
            Interaktivní rozpočet
          </h3>
          <p className="text-slate-300">
            V jednotlivých sekcích můžete <strong>odkliknout položky</strong>, které nepotřebujete, 
            a celková cena se automaticky přepočítá. DJ a Moderátor + základní technické zázemí jsou pevné položky.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// VIDEO GALLERY SECTION
const VideoGallerySection = () => {
  const videos = [
    { 
      id: 'fP87mouxM28', 
      title: 'Kapela Party Leaders', 
      description: '180 min živé vystoupení' 
    },
    { 
      id: 'oFDrCzH62w4', 
      title: 'Brazilské tanečnice Tradicion', 
      description: 'Profesionální show z Prahy' 
    },
    { 
      id: 'r5vasgw9JVQ', 
      title: 'Anta Agni - Světelná show', 
      description: '2 efektní vstupy z Bratislavy' 
    },
    { 
      id: 'b_wSDGGIPZw?start=118', 
      title: 'Slack Line Show', 
      description: '2 akrobaté z Prahy' 
    },
    { 
      id: 'fBi7dHIFN54?start=2', 
      title: 'AC/DC Revival', 
      description: 'Rocková tribute show' 
    },
    { 
      id: 'sOLwFqrnzmc', 
      title: 'Virtuální realita', 
      description: 'VR zážitky pro hosty' 
    }
  ];

  return (
    <section className="py-24 px-6 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center gap-4">
          <span className="text-6xl">🎬</span>
          UKÁZKY PROGRAMU
        </h2>
        <p className="text-slate-400 text-lg mb-12">
          Podívejte se na video ukázky jednotlivých show a vystoupení
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <div 
              key={index}
              className="bg-slate-900/40 border border-white/10 rounded-xl overflow-hidden hover:border-[#FF6B00]/50 transition-all group"
            >
              <div className="relative aspect-video bg-slate-950">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              <div className="p-4">
                <h3 className="text-white font-bold text-lg mb-1 group-hover:text-[#FF6B00] transition-colors">
                  {video.title}
                </h3>
                <p className="text-slate-400 text-sm">{video.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            💡 Všechna videa jsou pouze ukázková. Konkrétní program upravíme dle vašich požadavků.
          </p>
        </div>
      </div>
    </section>
  );
};

// DRAMATURGIE SECTION
const DramaturgieSection = ({ programItems, toggleProgramItem, production }) => {
  const [expandedSections, setExpandedSections] = useState({
    artists: true,
    arena: true,
    production: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <section className="py-24 px-6 bg-slate-950">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center gap-4">
          <span className="text-6xl">🎭</span>
          DRAMATURGIE DNE
        </h2>
        <p className="text-slate-400 text-lg mb-12">
          Program akce s možností úpravy podle vašich preferencí
        </p>

        {/* Artists */}
        <div className="bg-slate-900/40 border border-white/10 rounded-xl mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('artists')}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-900/60 transition-all"
          >
            <h3 className="text-2xl font-bold text-white">Umělci a show program</h3>
            {expandedSections.artists ? <ChevronUp className="w-6 h-6 text-[#FF6B00]" /> : <ChevronDown className="w-6 h-6 text-slate-400" />}
          </button>
          
          {expandedSections.artists && (
            <div className="p-6 pt-0 space-y-3 border-t border-white/5">
              {Object.entries(programItems)
                .filter(([key]) => !['rally', 'f1', 'vr', 'vr_1', 'vr_2', 'race_suit', 'hostess', 'winners_podium', 'foosball'].includes(key))
                .map(([key, item]) => (
                  <div 
                    key={key}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                      item.selected 
                        ? 'bg-[#FF6B00]/10 border-[#FF6B00]/30' 
                        : 'bg-slate-900/20 border-white/5'
                    } ${item.fixed ? 'opacity-100' : 'cursor-pointer hover:border-[#FF6B00]/50'}`}
                    onClick={() => !item.fixed && toggleProgramItem(key)}
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      item.selected 
                        ? 'bg-[#FF6B00] border-[#FF6B00]' 
                        : 'border-slate-600'
                    }`}>
                      {item.selected && <CheckCircle2 className="w-5 h-5 text-white" />}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold">{item.name}</span>
                        {item.recommended && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                        {item.fixed && <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">PEVNÁ POLOŽKA</span>}
                      </div>
                    </div>
                    
                    <div className="text-[#FF6B00] font-bold text-lg">
                      {item.price.toLocaleString('cs-CZ')} Kč
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Interactive Zone */}
        <div className="bg-slate-900/40 border border-white/10 rounded-xl mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('arena')}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-900/60 transition-all"
          >
            <h3 className="text-2xl font-bold text-white">Interaktivní zóna s aktivitami pro hosty</h3>
            {expandedSections.arena ? <ChevronUp className="w-6 h-6 text-[#FF6B00]" /> : <ChevronDown className="w-6 h-6 text-slate-400" />}
          </button>
          
          {expandedSections.arena && (
            <div className="p-6 pt-0 space-y-3 border-t border-white/5">
              <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 mb-4">
                <p className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Organizace závodů mezi hosty včetně vyhlášení vítězů na simulátorech je v ceně produkce a koordinace
                </p>
              </div>
              
              {Object.entries(programItems)
                .filter(([key]) => ['rally', 'f1', 'vr_1', 'vr_2', 'race_suit', 'hostess', 'winners_podium', 'foosball'].includes(key))
                .map(([key, item]) => (
                  <div 
                    key={key}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all cursor-pointer ${
                      item.selected 
                        ? 'bg-[#FF6B00]/10 border-[#FF6B00]/30' 
                        : 'bg-slate-900/20 border-white/5'
                    } hover:border-[#FF6B00]/50`}
                    onClick={() => toggleProgramItem(key)}
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      item.selected 
                        ? 'bg-[#FF6B00] border-[#FF6B00]' 
                        : 'border-slate-600'
                    }`}>
                      {item.selected && <CheckCircle2 className="w-5 h-5 text-white" />}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold">{item.name}</span>
                        {item.recommended && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                      </div>
                    </div>
                    
                    <div className="text-[#FF6B00] font-bold text-lg">
                      {item.price.toLocaleString('cs-CZ')} Kč
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Mezisoucet Program */}
        <div className="mt-8 bg-gradient-to-r from-[#FF6B00]/20 to-[#FF6B00]/10 border-2 border-[#FF6B00]/50 rounded-xl p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-white">CELKEM DRAMATURGIE A PROGRAM</h3>
              <p className="text-slate-400 text-sm mt-1">Moderátor, DJ a show program</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-[#FF6B00]">
                {Object.values(programItems)
                  .filter(item => item.selected)
                  .reduce((sum, item) => sum + item.price, 0).toLocaleString('cs-CZ')} Kč
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// TECHNICAL SECTION
const TechnicalSection = ({ technicalItems, toggleTechnicalItem }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <section className="py-24 px-6 bg-[#020617]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center gap-4">
          <span className="text-6xl">🏗️</span>
          TECHNICKÉ ZABEZPEČENÍ
        </h2>
        <p className="text-slate-400 text-lg mb-12">
          VIP stan, venkovní prostory, ozvučení, osvětlení, LED panely + personál (vše je nyní volitelné)
        </p>

        <div className="bg-slate-900/40 border border-white/10 rounded-xl overflow-hidden">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-900/60 transition-all"
          >
            <h3 className="text-2xl font-bold text-white">Kompletní technické vybavení</h3>
            {expanded ? <ChevronUp className="w-6 h-6 text-[#FF6B00]" /> : <ChevronDown className="w-6 h-6 text-slate-400" />}
          </button>
          
          {expanded && (
            <div className="p-6 pt-0 space-y-3 border-t border-white/5">
              {Object.entries(technicalItems)
                .filter(([key, item]) => !item.isAutomatic) // Skrýt automatické položky
                .map(([key, item]) => (
                <div key={key}>
                  <div 
                    onClick={() => toggleTechnicalItem(key)}
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                      item.selected 
                        ? 'bg-slate-900/20 border border-[#FF6B00]/30' 
                        : 'bg-slate-900/10 border border-white/5 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        item.selected 
                          ? 'bg-[#FF6B00] border-[#FF6B00]' 
                          : 'bg-transparent border-slate-600'
                      }`}>
                        {item.selected && <CheckCircle2 className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <span className={`font-semibold ${item.selected ? 'text-white' : 'text-slate-500'}`}>
                          {item.name}
                        </span>
                      </div>
                    </div>
                    
                    <div className={`font-bold text-lg ${item.selected ? 'text-[#FF6B00]' : 'text-slate-600'}`}>
                      {item.price.toLocaleString('cs-CZ')} Kč
                    </div>
                  </div>
                  
                  {/* Tent Photos - Display under the Modular Tent item */}
                  {key === 'tent' && (
                    <div className="mt-4 ml-10 p-4 bg-slate-900/30 border border-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-3 text-sm">
                        Vnitřní ukázka stanu vč. výzdoby a inventáře z naší realizace
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/3w7sptoj_dekorace%20high.jpg"
                          alt="Dekorace stanu - vnitřní výzdoba"
                          className="w-full h-64 object-cover rounded-lg border border-white/10"
                        />
                        <img 
                          src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/fszd8urc_stan%20podlaha.jpg"
                          alt="Stan podlaha - kompletní vybavení"
                          className="w-full h-64 object-cover rounded-lg border border-white/10"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* LED Panels Photos - Display under LED panels item */}
                  {key === 'led_panels' && (
                    <div className="mt-4 ml-10 p-4 bg-slate-900/30 border border-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-3 text-sm">
                        Vizualizace LED panelů. Denní formát, afterparty formát.
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <img 
                            src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/sehdfxpp_koroporatnivzhled.png.JPEG"
                            alt="LED panely - denní firemní formát"
                            className="w-full h-64 object-cover rounded-lg border border-white/10"
                          />
                          <p className="text-slate-400 text-xs mt-2 text-center">Denní formát</p>
                        </div>
                        <div>
                          <img 
                            src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ng3pidqf_vecerni%20vzhed%20LED.png.JPEG"
                            alt="LED panely - večerní afterparty formát"
                            className="w-full h-64 object-cover rounded-lg border border-white/10"
                          />
                          <p className="text-slate-400 text-xs mt-2 text-center">Afterparty formát</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Outdoor LED Screen Photos */}
                  {key === 'outdoor_led' && (
                    <div className="mt-4 ml-10 p-4 bg-slate-900/30 border border-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-3 text-sm">
                        Venkovní LED obrazovka z naších realizací
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/1gscci5a_Sni%CC%81mek%20obrazovky%202026-03-26%20v%C2%A013.04.22.png"
                          alt="LED obrazovka outdoor - venkovní akce"
                          className="w-full h-64 object-cover rounded-lg border border-white/10"
                        />
                        <img 
                          src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/y2g7zazl_Sni%CC%81mek%20obrazovky%202026-03-26%20v%C2%A013.04.13.png"
                          alt="LED obrazovka outdoor - večerní atmosféra"
                          className="w-full h-64 object-cover rounded-lg border border-white/10"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mezisoucet Technika */}
        <div className="mt-8 bg-gradient-to-r from-[#FF6B00]/20 to-[#FF6B00]/10 border-2 border-[#FF6B00]/50 rounded-xl p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-white">CELKEM TECHNICKÉ ZABEZPEČENÍ</h3>
              <p className="text-slate-400 text-sm mt-1">Vč. stanu, techniky a personálu</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-[#FF6B00]">
                {Object.values(technicalItems)
                  .filter(item => item.selected)
                  .reduce((sum, item) => sum + item.price, 0).toLocaleString('cs-CZ')} Kč
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// DECORATION SECTION
const DecorationSection = ({ decorationItems, toggleDecorationItem }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <section className="py-24 px-6 bg-[#020617]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center gap-4">
          <span className="text-6xl">🎨</span>
          DEKORACE
        </h2>
        <p className="text-slate-400 text-lg mb-8">
          Kompletní výzdoba ve firemních barvách - černo-oranžová kombinace
        </p>

        {/* Reference Photos Gallery */}
        <div className="mb-12 bg-slate-900/30 border border-white/5 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 text-lg">Referenční fotografie z našich realizací</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/w839qvml_dekorace%20GREINER.png"
              alt="Dekorace - vánoční večírek"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/7h8wlwol_dekorace.jpg"
              alt="Dekorace - plná hala s balonky"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/wpbq7rjq_dekorace3.jpg"
              alt="Dekorace - balonkový oblouk"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9n23usqw_dekorace4.jpg"
              alt="Dekorace - detail stolů s branding"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/kybwmtt0__AMK1267-3.jpg"
              alt="Dekorace - elegantní prostřené stoly s mašlemi"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ym9u4uid__AMK1281-2.jpg"
              alt="Dekorace - detail stolního uspořádání"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/kgprgnjv__AMK1378.jpg"
              alt="Dekorace - zlatá mašle na židli"
              className="w-full h-48 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
          </div>
        </div>

        <div className="bg-slate-900/40 border border-white/10 rounded-xl overflow-hidden">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-900/60 transition-all"
          >
            <h3 className="text-2xl font-bold text-white">Kompletní dekorace a vybavení</h3>
            {expanded ? <ChevronUp className="w-6 h-6 text-[#FF6B00]" /> : <ChevronDown className="w-6 h-6 text-slate-400" />}
          </button>
          
          {expanded && (
            <div className="p-6 pt-0 space-y-3 border-t border-white/5">
              {Object.entries(decorationItems).map(([key, item]) => (
                <div key={key}>
                  <div 
                    onClick={() => toggleDecorationItem(key)}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-900/20 border border-white/5 cursor-pointer hover:bg-slate-900/40 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        item.selected 
                          ? 'bg-[#FF6B00] border-[#FF6B00]' 
                          : 'bg-transparent border-slate-600'
                      }`}>
                        {item.selected && <CheckCircle2 className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <span className={`font-semibold ${item.selected ? 'text-white' : 'text-slate-500'}`}>
                          {item.name}
                        </span>
                      </div>
                    </div>
                    
                    <div className={`font-bold text-lg ${item.selected ? 'text-[#FF6B00]' : 'text-slate-600'}`}>
                      {item.price.toLocaleString('cs-CZ')} Kč
                    </div>
                  </div>
                  
                  {/* Cake Model Photo */}
                  {key === 'cake_model' && (
                    <div className="mt-3 ml-10 p-4 bg-slate-900/30 border border-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-3 text-sm">
                        Reference makety dortu z naší realizace
                      </h4>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/pb97zeau_att.6zotptkI1H-6xp1HU_r1DomLNk2Tr-EQo3-P9DsjrBc.JPG"
                        alt="Maketa dortu 2,5m - reference"
                        className="w-full max-w-md h-80 object-cover rounded-lg border border-white/10"
                      />
                    </div>
                  )}
                  
                  {/* Light Number 30 Photo */}
                  {key === 'light_number_30' && (
                    <div className="mt-3 ml-10 p-4 bg-slate-900/30 border border-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-3 text-sm">
                        Svítící číslo 30 - reference z realizace
                      </h4>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/lz9nqhde_c%CC%8Ci%CC%81slo%2030.png"
                        alt="Svítící číslo 30 na pódiu"
                        className="w-full max-w-md h-80 object-cover rounded-lg border border-white/10"
                      />
                    </div>
                  )}
                  
                  {/* Cupcakes Photo */}
                  {key === 'cupcakes' && (
                    <div className="mt-3 ml-10 p-4 bg-slate-900/30 border border-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-3 text-sm">
                        Cup cakes s logem firmy - reference
                      </h4>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/hiz0y2ir_att.AXaWm-35I47OGaLVUhkF_njcQ4robg75sEDtf1Rb6eA.JPG"
                        alt="Cup cakes s oranžovými dekoracemi"
                        className="w-full max-w-md h-80 object-cover rounded-lg border border-white/10"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mezisoucet Dekorace */}
        <div className="mt-8 bg-gradient-to-r from-[#FF6B00]/20 to-[#FF6B00]/10 border-2 border-[#FF6B00]/50 rounded-xl p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-white">CELKEM DEKORACE A VYBAVENÍ</h3>
              <p className="text-slate-400 text-sm mt-1">Vč. personálu na dekoraci</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-[#FF6B00]">
                {Object.values(decorationItems)
                  .filter(item => item.selected)
                  .reduce((sum, item) => sum + item.price, 0).toLocaleString('cs-CZ')} Kč
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// GRAPHICS SECTION
const GraphicsSection = ({ graphicsItems, toggleGraphicsItem }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <section className="py-24 px-6 bg-[#020617]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center gap-4">
          <span className="text-6xl">✨</span>
          GRAFIKA
        </h2>
        <p className="text-slate-400 text-lg mb-8">
          Kompletní grafické zpracování materiálů pro vaši akci
        </p>

        {/* Reference Photos Gallery */}
        <div className="mb-12 bg-slate-900/30 border border-white/5 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 text-lg">Referenční ukázky z našich realizací</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/cnj9xb0f__AMK1286.jpg"
              alt="Grafika - seatplan pódium"
              className="w-full h-96 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/vnto206m__AMK1271.jpg"
              alt="Grafika - vánoční pozvánka"
              className="w-full h-96 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
            />
          </div>
        </div>

        <div className="bg-slate-900/40 border border-white/10 rounded-xl overflow-hidden">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-900/60 transition-all"
          >
            <h3 className="text-2xl font-bold text-white">Grafické služby</h3>
            {expanded ? <ChevronUp className="w-6 h-6 text-[#FF6B00]" /> : <ChevronDown className="w-6 h-6 text-slate-400" />}
          </button>
          
          {expanded && (
            <div className="p-6 pt-0 space-y-3 border-t border-white/5">
              {Object.entries(graphicsItems).map(([key, item]) => (
                <div 
                  key={key}
                  onClick={() => toggleGraphicsItem(key)}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-900/20 border border-white/5 cursor-pointer hover:bg-slate-900/40 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      item.selected 
                        ? 'bg-[#FF6B00] border-[#FF6B00]' 
                        : 'bg-transparent border-slate-600'
                    }`}>
                      {item.selected && <CheckCircle2 className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                      <span className={`font-semibold ${item.selected ? 'text-white' : 'text-slate-500'}`}>
                        {item.name}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`font-bold text-lg ${item.selected ? 'text-[#FF6B00]' : 'text-slate-600'}`}>
                    {item.price.toLocaleString('cs-CZ')} Kč
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mezisoucet Grafika */}
        <div className="mt-8 bg-gradient-to-r from-[#FF6B00]/20 to-[#FF6B00]/10 border-2 border-[#FF6B00]/50 rounded-xl p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-white">CELKEM GRAFIKA</h3>
              <p className="text-slate-400 text-sm mt-1">Grafické práce a tiskoviny</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-[#FF6B00]">
                {Object.values(graphicsItems)
                  .filter(item => item.selected)
                  .reduce((sum, item) => sum + item.price, 0).toLocaleString('cs-CZ')} Kč
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// GRAND TOTAL
const GrandTotalSection = ({ totals, programItems, technicalItems, decorationItems, graphicsItems, production }) => {
  
  const generatePDF = () => {
    // Helper function to remove diacritics for better PDF compatibility
    const removeDiacritics = (str) => {
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ě/g, 'e')
        .replace(/Ě/g, 'E')
        .replace(/ř/g, 'r')
        .replace(/Ř/g, 'R')
        .replace(/č/g, 'c')
        .replace(/Č/g, 'C')
        .replace(/š/g, 's')
        .replace(/Š/g, 'S')
        .replace(/ž/g, 'z')
        .replace(/Ž/g, 'Z')
        .replace(/ý/g, 'y')
        .replace(/Ý/g, 'Y')
        .replace(/á/g, 'a')
        .replace(/Á/g, 'A')
        .replace(/í/g, 'i')
        .replace(/Í/g, 'I')
        .replace(/é/g, 'e')
        .replace(/É/g, 'E')
        .replace(/ú/g, 'u')
        .replace(/Ú/g, 'U')
        .replace(/ů/g, 'u')
        .replace(/Ů/g, 'U')
        .replace(/ó/g, 'o')
        .replace(/Ó/g, 'O')
        .replace(/ď/g, 'd')
        .replace(/Ď/g, 'D')
        .replace(/ť/g, 't')
        .replace(/Ť/g, 'T')
        .replace(/ň/g, 'n')
        .replace(/Ň/g, 'N');
    };
    
    const doc = new jsPDF();
    doc.setFont('helvetica');
    
    let yPos = 15;
    
    // Header with colored background
    doc.setFillColor(255, 107, 0);
    doc.rect(0, 0, 210, 35, 'F');
    
    // Title
    doc.setFontSize(24);
    doc.setTextColor(255, 255, 255);
    doc.text(removeDiacritics('20 let PS Plasty'), 105, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(removeDiacritics('Cenova kalkulace akce'), 105, 23, { align: 'center' });
    
    doc.setFontSize(9);
    doc.text(removeDiacritics('Datum: 18.9.2026 | Cas: 15:00-2:00 | Pocet hostu: ~80'), 105, 30, { align: 'center' });
    
    yPos = 45;
    
    // Helper function to add section
    const addSection = (title, items, total) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Section header
      doc.setFillColor(245, 245, 245);
      doc.rect(15, yPos - 5, 180, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(removeDiacritics(title), 20, yPos);
      yPos += 10;
      
      // Items
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      items.forEach(item => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.setTextColor(60, 60, 60);
        doc.text(removeDiacritics(`  ${item.name}`), 20, yPos);
        doc.text(removeDiacritics(`${item.price.toLocaleString('cs-CZ')} Kc`), 185, yPos, { align: 'right' });
        yPos += 5;
      });
      
      // Section total
      yPos += 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 107, 0);
      doc.text(removeDiacritics(`Celkem: ${total.toLocaleString('cs-CZ')} Kc`), 185, yPos, { align: 'right' });
      yPos += 10;
      doc.setFont('helvetica', 'normal');
    };
    
    // Program section
    const programItemsArray = Object.entries(programItems)
      .filter(([_, item]) => item.selected)
      .map(([_, item]) => ({ name: item.name, price: item.price }));
    
    if (programItemsArray.length > 0) {
      addSection('DRAMATURGIE A PROGRAM', programItemsArray, totals.program);
    }
    
    // Catering section
    addSection('CATERING & GASTRO', [
      { name: 'Kompletni catering (Obed, Coffee Break, Vecere, Napoje)', price: totals.catering }
    ], totals.catering);
    
    // Technical section
    const technicalItemsArray = Object.entries(technicalItems)
      .filter(([_, item]) => item.selected)
      .map(([_, item]) => ({ name: item.name, price: item.price }));
    
    if (technicalItemsArray.length > 0) {
      addSection('TECHNICKE ZABEZPECENI', technicalItemsArray, totals.technical);
    }
    
    // Decoration section
    const decorationItemsArray = Object.entries(decorationItems)
      .filter(([_, item]) => item.selected)
      .map(([_, item]) => ({ name: item.name, price: item.price }));
    
    if (decorationItemsArray.length > 0) {
      addSection('DEKORACE', decorationItemsArray, totals.decoration);
    }
    
    // Graphics section
    const graphicsItemsArray = Object.entries(graphicsItems)
      .filter(([_, item]) => item.selected)
      .map(([_, item]) => ({ name: item.name, price: item.price }));
    
    if (graphicsItemsArray.length > 0) {
      addSection('GRAFIKA', graphicsItemsArray, totals.graphics);
    }
    
    // Production section
    addSection('PRODUKCE A KOORDINACE', [
      { name: 'Produkce cele akce + koordinacni team', price: totals.production }
    ], totals.production);
    
    // Grand total
    yPos += 5;
    doc.setDrawColor(255, 107, 0);
    doc.setLineWidth(1);
    doc.line(15, yPos, 195, yPos);
    yPos += 8;
    
    doc.setFillColor(255, 107, 0);
    doc.rect(15, yPos - 6, 180, 12, 'F');
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(removeDiacritics('CELKOVA INVESTICE:'), 20, yPos);
    doc.text(removeDiacritics(`${totals.total.toLocaleString('cs-CZ')} Kc`), 185, yPos, { align: 'right' });
    
    yPos += 8;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(removeDiacritics('(Ceny bez DPH 21%)'), 185, yPos, { align: 'right' });
    
    // Footer
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(removeDiacritics('Nabidku pripravila: VSE PRO AKCI s.r.o.'), 105, 280, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('www.vseproakci.cz | info@vseproakci.cz', 105, 285, { align: 'center' });
    
    // Save PDF
    doc.save('Silent_Czech_30_Rozpocet.pdf');
  };
  
  return (
    <section className="py-24 px-6 bg-gradient-to-b from-[#020617] to-slate-950">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
        Cenová kalkulace
      </h2>

      <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-8 md:p-12">
        <div className="space-y-4 mb-8">
          <div className="flex justify-between items-center py-4 border-b border-white/10">
            <span className="text-slate-300 text-lg">Dramaturgie a program</span>
            <span className="font-bold text-white text-xl">{totals.program.toLocaleString('cs-CZ')} Kč</span>
          </div>

          <div className="flex justify-between items-center py-4 border-b border-white/10">
            <span className="text-slate-300 text-lg">Catering & gastro</span>
            <span className="font-bold text-white text-xl">{totals.catering.toLocaleString('cs-CZ')} Kč</span>
          </div>

          <div className="flex justify-between items-center py-4 border-b border-white/10">
            <span className="text-slate-300 text-lg">Technické zabezpečení</span>
            <span className="font-bold text-white text-xl">{totals.technical.toLocaleString('cs-CZ')} Kč</span>
          </div>

          <div className="flex justify-between items-center py-4 border-b border-white/10">
            <span className="text-slate-300 text-lg">Dekorace</span>
            <span className="font-bold text-white text-xl">{totals.decoration.toLocaleString('cs-CZ')} Kč</span>
          </div>

          <div className="flex justify-between items-center py-4 border-b border-white/10">
            <span className="text-slate-300 text-lg">Grafika</span>
            <span className="font-bold text-white text-xl">{totals.graphics.toLocaleString('cs-CZ')} Kč</span>
          </div>

          <div className="flex justify-between items-center py-4 border-b border-white/10">
            <span className="text-slate-300 text-lg">Produkce a koordinace</span>
            <span className="font-bold text-white text-xl">{totals.production.toLocaleString('cs-CZ')} Kč</span>
          </div>
        </div>

        <div className="pt-8 border-t-2 border-[#FF6B00]/50">
          <div className="flex justify-between items-center mb-2">
            <span className="text-2xl font-bold text-white">CELKOVÁ INVESTICE</span>
            <div className="text-4xl font-bold" style={{
              background: 'linear-gradient(135deg, #FF6B00 0%, #FFB800 50%, #FF6B00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {totals.total.toLocaleString('cs-CZ')} Kč
            </div>
          </div>
          <p className="text-sm text-slate-400 text-right">Ceny bez DPH 21%</p>
        </div>
      </div>

      {/* Export Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={generatePDF}
          className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#FF6B00] to-[#FFB800] text-white font-bold text-lg rounded-xl hover:scale-105 transition-all shadow-2xl hover:shadow-[#FF6B00]/50"
        >
          <Download className="w-6 h-6 group-hover:animate-bounce" />
          Stáhnout rozpočet PDF
        </button>
      </div>

      <div className="mt-8 text-center">
        <p className="text-slate-400 text-sm">
          💡 Celková cena se automaticky přepočítá podle vybraných položek
        </p>
        <p className="text-slate-500 text-xs mt-2">
          Nabídka připravena agenturou <strong className="text-white">VŠE PRO AKCI s.r.o.</strong>
        </p>
      </div>
    </div>
  </section>
  );
};

// PROMO VIDEO SECTION
const PromoVideoSection = () => (
  <section className="py-24 px-6 bg-gradient-to-b from-slate-950 to-[#020617]">
    <div className="max-w-7xl mx-auto">
      <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 text-center">
        Jak vypadá realizace VIP eventů z naší produkce?
      </h2>
      <p className="text-slate-400 text-lg mb-12 text-center max-w-3xl mx-auto">
        Podívejte se na ukázky z našich realizací a přesvědčte se o kvalitě naší práce
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Video 1 */}
        <div className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-[#FF6B00]/50 transition-all">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/Z9URBj-DJSE"
            title="VIP Event realizace 1"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        
        {/* Video 2 */}
        <div className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-[#FF6B00]/50 transition-all">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/P48ULgwUVuA"
            title="VIP Event realizace 2"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        
        {/* Video 3 */}
        <div className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-[#FF6B00]/50 transition-all">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/9-H7iHXLIWo"
            title="VIP Event realizace 3"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        
        {/* Video 4 */}
        <div className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-[#FF6B00]/50 transition-all">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/0o9B-dW8x3k"
            title="VIP Event realizace 4"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        
        {/* Video 5 */}
        <div className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-[#FF6B00]/50 transition-all">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/QFfg6mOml4s"
            title="VIP Event realizace 5"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        
        {/* Empty placeholder for grid alignment */}
        <div className="aspect-video rounded-xl bg-slate-900/30 border border-white/5 flex items-center justify-center">
          <p className="text-slate-500 text-sm">A mnoho dalších...</p>
        </div>
      </div>
    </div>
  </section>
);

// WHY US SECTION
const WhyUsSection = () => (
  <section className="py-24 px-6 bg-[#020617]">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
        Proč organizovat event s námi?
      </h2>
      
      <div className="bg-slate-900/40 border border-white/10 rounded-2xl p-8 md:p-12">
        <div className="space-y-6 text-slate-300 text-lg leading-relaxed">
          <p>
            <span className="font-bold text-white">Nejsme pouze produkční agentura.</span> Jsme realizační společnost s vlastním technickým 
            i cateringovým zázemím a týmem zkušených profesionálů – od kuchařů a techniků až 
            po produkční a logistiku.
          </p>
          
          <p>
            Díky vlastnímu vybavení a internímu týmu máme <span className="font-bold text-white">celý event plně pod kontrolou</span>. 
            Nejsme závislí na externích dodavatelích, což nám umožňuje garantovat kvalitu, přesné 
            časování i transparentní rozpočet bez zbytečných provizí.
          </p>
          
          <p>
            Klient tak získává partnera, který má realizaci skutečně ve vlastních rukou – <span className="font-bold text-white">od prvního 
            návrhu až po poslední detail akce</span>.
          </p>
          
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-2xl md:text-3xl font-serif italic text-center mb-8" style={{
              color: '#FF6B00'
            }}>
              Za kvalitu i průběh realizace osobně ručím.
            </p>
            
            <div className="flex flex-col items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/99766hss_podpis-vodarek-final.png"
                alt="Podpis Jakub Vodárek"
                className="h-24 md:h-32 w-auto"
              />
              <p className="text-slate-400">Jednatel společnosti</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// CONTACT
const ContactSection = () => (
  <footer className="py-16 px-6 bg-[#020617] border-t border-white/10">
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Kontakt</h3>
          <div className="space-y-3 text-slate-300">
            <p><strong className="text-white">VŠE PRO AKCI s.r.o.</strong></p>
            <p>Ondruchová Monika</p>
            <p>Event Manager</p>
            <p>📞 +420 773 550 880</p>
            <p>📧 info@vseproakci.cz</p>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Event Info</h3>
          <div className="space-y-2 text-slate-300">
            <p><strong className="text-white">Datum:</strong> 24. září 2026 (čtvrtek)</p>
            <p><strong className="text-white">Čas:</strong> 12:00 - 24:00</p>
            <p><strong className="text-white">Místo:</strong> Areál firmy PS Plasty</p>
            <p><strong className="text-white">Počet hostů:</strong> cca 200</p>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-white/10 text-center text-slate-500 text-sm">
        <p>20 let PS Plasty • Výročí úspěchu a růstu</p>
      </div>
    </div>
  </footer>
);

export default PSPlastyPage;
